// ---------------------------------------------------------------------------
// The numbers board dealing itself.
//
// The chooser has said "two large, please" and pressed one button. Everything
// after that is watching: six tiles arrive one at a time, then the target spins
// up and lands, and only then does anybody's clock start.
//
// **The spin is honest, and that is the whole design.** The server sends the
// six tiles as six separate actions and the target as a seventh, each on its own
// beat, so at no point does this client hold a number it is not meant to be
// showing — there is nothing in the state to read ahead, nothing in the socket
// frame to peek at, and nothing for a sanitizer bug to leak. The digits flickering
// during the spin are this component's own `Math.random`, invented here and
// thrown away; the real target replaces them the moment it arrives over the wire.
// A version that received the whole board up front and animated it would look
// identical and be a lie, and one open network tab would be the whole round.
//
// Nothing on this screen is interactive. It is the one phase of the game where
// the correct thing for every player to do is nothing at all, which is why the
// only clock drawn is the deal's own progress and not a countdown anybody is
// spending.
//
// It also hands its geometry on. The six tiles and the target do not vanish when
// the phase changes — they are the same seven objects the play screen opens
// with, in different places — so this screen records where they were and
// `takeNumbersBoardHandoff` gives that to whatever renders next. See the note on
// that function, and the letters round's `takeBoardHandoff`, which is the same
// device.
// ---------------------------------------------------------------------------

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import {
  NUMBERS_TARGET_MAX,
  NUMBERS_TARGET_MIN,
  NUMBERS_TILE_COUNT,
  TILE_FLIP_MS,
} from '@countdown/shared';
import { NumberTile } from '../../components/primitives/NumberTile.js';
import { StandingsStrip } from '../../components/primitives/StandingsStrip.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import { roundLabel } from '../../lib/format.js';
import { useSettingsStore } from '../../store/settingsStore.js';
import type { RoomScreenProps } from './types.js';

const MONO_LABEL = 'font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-dim';

/** A box on the screen, in viewport coordinates. */
interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Where the dealt board sat, for the play screen to animate out of.
 *
 * The same device as `takeBoardHandoff` in `LettersSelectionScreen`, for the
 * same reason: the last thing this screen shows and the first thing the play
 * screen shows are the same seven objects in two different places, and the two
 * screens never coexist — the phase change swaps one for the other between
 * frames — so there is no shared-element capture to take at the swap. It is
 * measured here while the board is still up and read back one screen later.
 *
 * `tiles` is always six entries, in board order. A slot that had not been dealt
 * is a zero-width rect rather than a missing entry, so the reader indexes slots
 * rather than counting them; `target` is null until the number has landed.
 */
export interface NumbersBoardHandoff {
  /** The round it was measured on. A different round makes it stale. */
  round: number;
  /** When, so a rect from a screen the player has since left is not reused. */
  at: number;
  tiles: Rect[];
  target: Rect | null;
}

let handoff: NumbersBoardHandoff | null = null;

/**
 * The dealt board's geometry, once.
 *
 * **Taking it clears it.** A second read is a screen that arrived some other
 * way — a reload straight into `numbers-play`, a late join, a restored room —
 * and it must not slide a board out of positions that were never on this
 * device.
 */
export function takeNumbersBoardHandoff(): NumbersBoardHandoff | null {
  const held = handoff;
  handoff = null;
  return held;
}

function rectOf(element: Element | null): Rect | null {
  if (element === null) return null;
  const rect = element.getBoundingClientRect();
  return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
}

const ZERO_RECT: Rect = { left: 0, top: 0, width: 0, height: 0 };

/**
 * How long to wait before measuring the target a second time.
 *
 * The panel eases from `scale-[0.97]` to `scale-100` over 200ms as the number
 * lands, and `getBoundingClientRect` reads through a transform — so a rect taken
 * on the frame the target arrives is the box mid-transition rather than the box
 * the player is looking at when the phase changes. The deal holds for
 * `NUMBERS_TARGET_HOLD_MS` after that, so there is room to measure the settled
 * one and it is the settled one the next screen has to start from.
 */
const TARGET_SETTLE_MS = 240;

/**
 * How often the spinning target changes digits.
 *
 * Fast enough to read as a blur rather than as a sequence of numbers somebody
 * could follow, slow enough that it is not a strobe. It is not tied to
 * `NUMBERS_TARGET_SPIN_MS`: the spin ends when the real target ARRIVES, not
 * when a local timer says it should, so this only decides what the wait looks
 * like and never how long it is.
 */
const SPIN_TICK_MS = 70;

const TILE_BACK = 'linear-gradient(var(--c-tile-back-top), var(--c-tile-back-bottom))';
const TILE_BACK_EDGE = 'inset 0 1px 0 var(--c-tile-back-edge)';

/** A number in the target's range that is not the target. */
function spinDigits(): number {
  return NUMBERS_TARGET_MIN + Math.floor(Math.random() * (NUMBERS_TARGET_MAX - NUMBERS_TARGET_MIN + 1));
}

/**
 * The target, or something that is not it yet.
 *
 * Held in state and stepped on an interval rather than driven by CSS, because
 * the digits have to be real text: a screen reader gets `aria-live="off"`
 * during the spin and the settled value announced once when it lands, which a
 * CSS-only shuffle could not do and which would otherwise read out forty
 * meaningless numbers.
 */
function TargetPanel({
  target,
  digitRef,
}: {
  target: number | null;
  /** The digits themselves, which is what travels to the next screen. */
  digitRef: RefObject<HTMLSpanElement | null>;
}) {
  const isDesktop = useIsDesktop();
  // Subscribed rather than read once, so turning motion off mid-round stops the
  // spin rather than waiting for the next numbers game.
  const reducedMotion = useSettingsStore((store) => store.reducedMotion) === true;
  const [shown, setShown] = useState<number>(spinDigits);

  // The settled target is never copied into state. It is already a prop, the
  // render below prefers it, and writing it here would be a second copy of one
  // value that the React Compiler rules rightly refuse — `setState` inside an
  // effect to mirror a prop is the cascade those rules exist to stop.
  //
  // Reduced motion gets a still placeholder rather than a flicker. The wait is
  // the same length either way, because the server decides when the target
  // arrives; only the strobing is skipped.
  useEffect(() => {
    if (target !== null || reducedMotion) return;
    const timer = setInterval(() => setShown(spinDigits()), SPIN_TICK_MS);
    return () => clearInterval(timer);
  }, [target, reducedMotion]);

  const settled = target !== null;

  return (
    <div className="flex flex-col items-center gap-2">
      <span className={MONO_LABEL}>{settled ? 'target' : 'and the target is'}</span>
      <div
        className={[
          'grid place-items-center rounded-frame border px-6 tabular font-mono font-extrabold leading-none',
          'transition-[border-color,color,transform] duration-200 ease-out',
          settled
            ? 'border-accent text-accent scale-100'
            : 'border-line-strong text-ink-dim scale-[0.97]',
        ].join(' ')}
        style={{
          fontSize: isDesktop ? 64 : 44,
          height: isDesktop ? 108 : 78,
          minWidth: isDesktop ? 220 : 160,
        }}
        // Silent while it is meaningless. The settled value is announced once,
        // by the `key` below forcing a fresh node the moment it lands.
        aria-live={settled ? 'polite' : 'off'}
      >
        <span ref={digitRef} key={settled ? 'settled' : 'spinning'}>
          {settled ? target : shown}
        </span>
      </div>
    </div>
  );
}

/**
 * One board position: a number that has arrived, or the slot it will land in.
 *
 * A tile that has just arrived turns over, using the same two-faced rotator as
 * the letters board — `tile-flip-in` with the back counter-rotated — so the two
 * boards deal in one visual language rather than two. It is the SCREEN that
 * decides a tile is arriving rather than `NumberTile`, which stays the plain
 * face it is on the play screen and in the pad.
 */
function Slot({ value, arriving, size }: { value: number | null; arriving: boolean; size: 'phone' | 'desktop' }) {
  const geometry = size === 'desktop' ? { width: 118, height: 108, radius: 12 } : { width: 48, height: 48, radius: 8 };

  if (value === null) {
    return (
      <div
        className="shrink-0"
        style={{
          width: geometry.width,
          height: geometry.height,
          borderRadius: geometry.radius,
          background: TILE_BACK,
          boxShadow: TILE_BACK_EDGE,
        }}
        aria-hidden
      />
    );
  }

  const face = <NumberTile value={value} state="available" size={size} />;
  if (!arriving) return face;

  return (
    <div className="shrink-0" style={{ width: geometry.width, height: geometry.height, perspective: 600 }}>
      <div className="tile-flip-in relative h-full w-full">
        <div
          className="tile-flip-back absolute inset-0"
          style={{ borderRadius: geometry.radius, background: TILE_BACK, boxShadow: TILE_BACK_EDGE }}
        />
        {face}
      </div>
    </div>
  );
}

export function NumbersDrawingScreen({ state }: RoomScreenProps) {
  const round = state.rounds[state.currentRoundIndex] ?? null;
  const tiles = round?.numberTiles ?? [];
  const target = round?.target ?? null;
  const isDesktop = useIsDesktop();
  const size = isDesktop ? 'desktop' : 'phone';

  // Which position is mid-flip. Only the newest tile animates: re-running the
  // flip on all of them whenever one arrives is what a naive keyed list does,
  // and it makes the board pulse every 620ms instead of dealing.
  const [arrivingIndex, setArrivingIndex] = useState<number | null>(null);
  const seen = useRef(0);

  useEffect(() => {
    if (tiles.length <= seen.current) {
      // A round starting over, or a restored room arriving with tiles already
      // up: adopt the count without animating any of them. A snapshot restored
      // mid-deal must not replay the four tiles it already dealt.
      seen.current = tiles.length;
      return;
    }
    seen.current = tiles.length;
    // The newest tile is at the FRONT of `numberTiles` and lands in the
    // leftmost slot still empty, because the board deals right to left so the
    // large tiles finish on the left. See `revealNumberTile`.
    const index = NUMBERS_TILE_COUNT - tiles.length;
    setArrivingIndex(index);
    const timer = setTimeout(() => setArrivingIndex((current) => (current === index ? null : current)), TILE_FLIP_MS);
    return () => clearTimeout(timer);
  }, [tiles.length]);

  // Six slots from the first frame, filled from the right. `numberTiles` is
  // board order and grows at the front, so a board of three tiles occupies the
  // three RIGHTMOST slots and the empty ones wait on the left — which is the
  // deal running towards the player rather than away from them.
  const firstFilled = NUMBERS_TILE_COUNT - tiles.length;
  const slots = Array.from({ length: NUMBERS_TILE_COUNT }, (_, index) =>
    index < firstFilled ? null : (tiles[index - firstFilled] ?? null),
  );

  // The board's geometry, kept up to date for whatever screen comes next.
  //
  // Written on every arrival rather than once at the end, because there is no
  // frame in which this screen knows it is about to be replaced: the phase
  // change arrives from the server and the swap happens between renders. So the
  // last write before that is the one that gets read, and the last write is the
  // finished board.
  //
  // Nothing here decides whether an animation happens. The reader takes it,
  // checks the round and the age, and asks the settings store itself.
  const boardRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef<HTMLSpanElement | null>(null);
  const roundIndex = round?.index;

  useLayoutEffect(() => {
    const row = boardRef.current;
    if (row === null || roundIndex === undefined) return;

    const capture = () => {
      handoff = {
        round: roundIndex,
        at: Date.now(),
        tiles: Array.from(row.children).map((child) => rectOf(child) ?? ZERO_RECT),
        target: target === null ? null : rectOf(targetRef.current),
      };
    };

    capture();
    if (target === null) return;
    // Once more, after the panel has finished growing into its settled size.
    // See TARGET_SETTLE_MS.
    const settle = window.setTimeout(capture, TARGET_SETTLE_MS);
    return () => window.clearTimeout(settle);
  }, [tiles.length, target, roundIndex]);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 md:gap-8 md:p-6">
      <StandingsStrip state={state} showRoundCounter />

      <header className="flex items-center gap-3">
        <span className="rounded-[5px] bg-raised px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-ink">
          {`${roundLabel(round?.type ?? 'numbers').toLowerCase()} ${(round?.index ?? state.currentRoundIndex) + 1} of ${state.totalRounds}`}
        </span>
        {/* What was actually called, which is the only thing anybody chose and
            the only thing on this screen that is not about to be obvious. */}
        {round?.largeCount !== null && round?.largeCount !== undefined && (
          <span className={`${MONO_LABEL} ml-auto`}>
            {round.largeCount === 1 ? '1 large' : `${round.largeCount} large`}
          </span>
        )}
      </header>

      <div className="flex flex-col items-center gap-8 md:gap-10">
        <div ref={boardRef} data-board className="flex flex-wrap justify-center gap-1 md:gap-3">
          {slots.map((value, index) => (
            <Slot
              key={index}
              value={value}
              arriving={arrivingIndex === index}
              size={size}
            />
          ))}
        </div>

        <TargetPanel target={target} digitRef={targetRef} />
      </div>
    </div>
  );
}
